import { IconEmail2 } from 'central-icons/IconEmail2';
import { IconTelephone } from 'central-icons/IconTelephone';

import { SmallLinkedinIcon } from '@revelio/assets';
import { Link, Text } from '@revelio/ui';

import { normalizeProfileUrl } from '../../../utils/normalizeProfileUrl';
import BlurredCell from '../../BlurredCell';
import styles from './card-sections.module.css';
import layoutStyles from './contact-info-section.module.css';

interface ContactInfoSectionProps {
  email?: string;
  personalEmail?: string;
  phoneNumber?: string;
  profileUrl?: string;
  isTrialUser?: boolean;
  encryptedEmail?: string;
  encryptedPersonalEmail?: string;
  encryptedPhoneNumber?: string;
  encryptedProfileUrl?: string;
  userKey?: string;
  onBlurHover?: () => void;
}

const noop = () => undefined;

export const ContactInfoSection = ({
  email,
  personalEmail,
  phoneNumber,
  profileUrl,
  isTrialUser = false,
  encryptedEmail,
  encryptedPersonalEmail,
  encryptedPhoneNumber,
  encryptedProfileUrl,
  userKey,
  onBlurHover,
}: ContactInfoSectionProps) => {
  const normalizedProfileUrl =
    profileUrl && profileUrl.trim() ? normalizeProfileUrl(profileUrl) : null;
  const hasAnyContact =
    Boolean(email?.trim()) ||
    Boolean(personalEmail?.trim()) ||
    Boolean(phoneNumber?.trim()) ||
    Boolean(normalizedProfileUrl);

  if (!hasAnyContact) return null;

  return (
    <div className={layoutStyles.contactList}>
      {email && (
        <div className={layoutStyles.cardEmail} data-testid="card-email">
          <div className={layoutStyles.contactIcon}>
            <IconEmail2 size={16} />
          </div>
          {isTrialUser ? (
            <BlurredCell
              value={email}
              encryptedValue={encryptedEmail}
              onMouseEnter={onBlurHover ?? noop}
              allowUnblur={false}
              isCompact={false}
              userKey={userKey}
              fieldKey="email"
            />
          ) : (
            <Link
              href={`mailto:${email}`}
              className={styles.contactLink}
              onClick={(e) => e.stopPropagation()}
            >
              {email}
            </Link>
          )}
        </div>
      )}

      {personalEmail && (
        <div
          className={layoutStyles.personalEmail}
          data-testid="card-personal-email"
        >
          <div className={layoutStyles.contactIcon}>
            <IconEmail2 size={16} />
          </div>
          {isTrialUser ? (
            <BlurredCell
              value={personalEmail}
              encryptedValue={encryptedPersonalEmail}
              onMouseEnter={onBlurHover ?? noop}
              allowUnblur={false}
              isCompact={false}
              userKey={userKey}
              fieldKey="personal_email"
            />
          ) : (
            <Link
              href={`mailto:${personalEmail}`}
              className={styles.contactLink}
              onClick={(e) => e.stopPropagation()}
            >
              {personalEmail}
            </Link>
          )}
        </div>
      )}

      {phoneNumber && (
        <div className={layoutStyles.cardEmail} data-testid="card-phone-number">
          <div className={layoutStyles.contactIcon}>
            <IconTelephone size={16} />
          </div>
          {isTrialUser ? (
            <BlurredCell
              value={phoneNumber}
              encryptedValue={encryptedPhoneNumber}
              onMouseEnter={onBlurHover ?? noop}
              allowUnblur={false}
              isCompact={false}
              userKey={userKey}
              fieldKey="phone_number"
            />
          ) : (
            <Text fontSize="sm" className={styles.contactLink}>
              {phoneNumber}
            </Text>
          )}
        </div>
      )}

      {normalizedProfileUrl && (
        <div className={layoutStyles.cardEmail} data-testid="card-profile-url">
          <div className={layoutStyles.linkedinIcon}>
            <SmallLinkedinIcon
              width="12px"
              height="12px"
              color="currentColor"
              display="block"
            />
          </div>
          {isTrialUser ? (
            <BlurredCell
              value={normalizedProfileUrl.displayValue}
              encryptedValue={encryptedProfileUrl}
              onMouseEnter={onBlurHover ?? noop}
              allowUnblur={false}
              isCompact={false}
              userKey={userKey}
              fieldKey="linkedin_url"
            />
          ) : (
            <Link
              href={normalizedProfileUrl.href}
              className={styles.contactLink}
              onClick={(e) => e.stopPropagation()}
              target="_blank"
              rel="noopener noreferrer"
            >
              {normalizedProfileUrl.displayValue}
            </Link>
          )}
        </div>
      )}
    </div>
  );
};
